import { createRouter, createWebHistory } from 'vue-router'

/**
 * Routes are lazy so the initial bundle is the shell plus whichever register the
 * user landed on, rather than every page in the app.
 */
export const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/', redirect: '/dashboard' },
    { path: '/dashboard', name: 'dashboard', component: () => import('./pages/Dashboard.vue'), meta: { title: 'Overview' } },
    { path: '/assets', name: 'assets', component: () => import('./pages/Assets.vue'), meta: { title: 'Asset register' } },
    // Registered before /assets/:id so "new" is not read as an asset id.
    { path: '/assets/new', name: 'asset-new', component: () => import('./pages/AssetNew.vue'), meta: { title: 'New asset' } },
    { path: '/assets/:id', name: 'asset', component: () => import('./pages/AssetDetail.vue'), meta: { title: 'Asset' } },
    { path: '/soa/:id', name: 'soa-entry', component: () => import('./pages/SoaEntry.vue'), meta: { title: 'Control' } },
    { path: '/proposals', name: 'proposals', component: () => import('./pages/Proposals.vue'), meta: { title: 'Proposals' } },
    { path: '/documents', name: 'documents', component: () => import('./pages/Documents.vue'), meta: { title: 'Documents' } },
    { path: '/readiness', name: 'readiness', component: () => import('./pages/Readiness.vue'), meta: { title: 'Readiness' } },
    { path: '/meetings', name: 'meetings', component: () => import('./pages/Meetings.vue'), meta: { title: 'Management review' } },
    { path: '/suppliers', name: 'suppliers', component: () => import('./pages/Suppliers.vue'), meta: { title: 'Suppliers' } },
    { path: '/ai-tools', name: 'ai-tools', component: () => import('./pages/AiTools.vue'), meta: { title: 'AI tooling' } },
    { path: '/attest', name: 'attest', component: () => import('./pages/Campaigns.vue'), meta: { title: 'Attestations' } },
    { path: '/attest/:id', name: 'attest-task', component: () => import('./pages/Attest.vue'), meta: { title: 'Attestation' } },
    { path: '/ropa', name: 'ropa', component: () => import('./pages/Ropa.vue'), meta: { title: 'Processing activities' } },
    { path: '/ropa/:id', name: 'ropa-activity', component: () => import('./pages/RopaDetail.vue'), meta: { title: 'Processing activity' } },
    { path: '/checks', name: 'checks', component: () => import('./pages/Checks.vue'), meta: { title: 'Checks' } },
    {
      path: '/checks/:id',
      name: 'check-run',
      component: () => import('./pages/CheckRun.vue'),
      // fullscreen: performing a check is a task done on a phone while walking. The app shell
      // spends a third of that screen on navigation the task does not need.
      meta: { title: 'Office security check', fullscreen: true },
    },
    { path: '/registers', name: 'registers', component: () => import('./pages/Registers.vue'), meta: { title: 'Reference data' } },
    { path: '/risks', name: 'risks', component: () => import('./pages/Risks.vue'), meta: { title: 'Risk register' } },
    { path: '/risks/new', name: 'risk-new', component: () => import('./pages/RiskNew.vue'), meta: { title: 'New risk' } },
    { path: '/risks/:id', name: 'risk', component: () => import('./pages/RiskDetail.vue'), meta: { title: 'Risk' } },
    { path: '/soa', name: 'soa', component: () => import('./pages/Soa.vue'), meta: { title: 'Statement of Applicability' } },
    { path: '/review', name: 'review', component: () => import('./pages/Review.vue'), meta: { title: 'Discoveries' } },
    { path: '/access', name: 'access', component: () => import('./pages/KeyCards.vue'), meta: { title: 'Key cards' } },
    { path: '/people', name: 'people', component: () => import('./pages/People.vue'), meta: { title: 'People & roles' } },
    { path: '/admin', name: 'admin', component: () => import('./pages/Admin.vue'), meta: { title: 'Roles & access' } },
    // MSAL returns here; initAuth has already consumed the response by the time the
    // router runs, so this only needs to get the user back to where they were.
    { path: '/auth/callback', redirect: '/dashboard' },
    { path: '/:pathMatch(.*)*', component: () => import('./pages/NotFound.vue') },
  ],
})

router.afterEach((to) => {
  const t = to.meta.title as string | undefined
  document.title = t ? `${t} — ABAX Asset and Risk` : 'ABAX Asset and Risk'
})

/**
 * Recover from a deploy that happened while the app was open.
 *
 * Route components are lazily imported from hashed filenames. A new deploy replaces those files,
 * so a tab loaded against the previous build asks for a chunk that no longer exists and the
 * navigation dies with "Failed to fetch dynamically imported module". Nothing in the app is wrong
 * and nothing the user does will fix it except a hard reload, which they have no reason to guess
 * at — the symptom is a button that does nothing.
 *
 * So the failure is treated as what it is: this tab is running yesterday's code. A full page load
 * of the intended route fetches the current build and continues where the user was going.
 * sessionStorage survives that load, which matters because the risk form receives its marked
 * suggestions through it.
 */
const RELOAD_GUARD = 'qms.reloadedForRoute'

function isStaleChunkError(error: unknown): boolean {
  const msg = error instanceof Error ? `${error.name}: ${error.message}` : String(error)
  return /dynamically imported module|Importing a module script failed|Loading chunk .* failed|error loading dynamically imported module/i.test(msg)
}

router.onError((error, to) => {
  if (!isStaleChunkError(error)) return

  // Reload once per target. Without the guard a chunk that is genuinely missing — a bad build
  // rather than a stale tab — would reload forever.
  if (sessionStorage.getItem(RELOAD_GUARD) === to.fullPath) {
    console.error(
      'Still cannot load the code for this page after reloading. This is a deployment problem ' +
      'rather than a stale tab.', error,
    )
    return
  }
  sessionStorage.setItem(RELOAD_GUARD, to.fullPath)
  window.location.assign(to.fullPath)
})

// A successful navigation means the tab is current again, so a later failure is allowed its reload.
router.afterEach(() => {
  sessionStorage.removeItem(RELOAD_GUARD)
})
